/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Auth Sagas
 *
 * @author Evgenij Tyan
 * @author Mikhail Kokoulin
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Andrey Dubov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.0.0
 */
import { createElement } from 'react';

import find from 'lodash/find';
import { isIE } from 'react-device-detect';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import FooterErrorModal, {
    IFooterErrorModalProps,
} from '@/main/components/Table/components/footerModal/FooterErrorModal';
import appExternalRedirect from '@/main/modules/app/appExternalRedirect';
import { IModalRenderProps } from '@/main/modules/modal/types/modalTypes';
import { setNotificationsSettings } from '@/main/modules/userNotifications/slice';
import { requestSaga } from '@/main/sagas/requestSaga';
import BrowserWarningModal from '@components/Table/components/footerModal/BrowserWarningModal';
import { API_HOSTNAME, API_LOGOUT, USER_AUTH } from '@modules/app/Api';
import { HttpStatus } from '@modules/app/appConstants';
import appHistory, { appHistoryPush } from '@modules/app/appHistory';
import { makeSelectAuthSSO } from '@modules/app/appSelectors';
import { EModeSSO, IAuthSSO } from '@modules/app/appTypes';
import { LOAD_INTERNAL_TRANSLATIONS_SUCCESS } from '@modules/internationalization/constants';
import { makeSelectTranslation, makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { setTextLoader } from '@modules/loader/loaderActions';
import { modalCloseAll, modalOpen } from '@modules/modal/modalActions';
import {
    closeNotificationAction,
    getNotificationsStart,
    openNotification,
} from '@modules/notification/notificationActions';
import { ENotificationLevelTypes } from '@modules/notification/types/notificationTypes';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { removeStorageDataStart, setLocalStorageDataAction } from '@modules/storage/storageActions';
import { makeSelectCookies, makeSelectLocalStorageCurrentUserId } from '@modules/storage/storageSelectors';
import { ECookieKey, ELocalStorageKeys, ICookies } from '@modules/storage/storageTypes';
import { RoutePath } from '@routes/constants/routesConstants';

import {
    authSetIsForbidden,
    loadAuthDataFailure,
    loadAuthDataSuccessful,
    logoutStartFailure,
    logoutStartSuccessful,
    redirectToSohoFailure,
} from '../actions/authActions';
import { cacheUserDataStart, cacheUserDataStartSuccessful } from '../actions/authCacheActions';
import {
    loginProcessNewsServiceAccessSuccessfulAction,
    loginProcessServiceAccessSuccessfulAction,
} from '../actions/authServiceAccessActions';
import { getTemporaryPassword } from '../actions/changePasswordActions';
import { CACHE_USER_DATA_START, CACHE_USER_DATA_START_SUCCESSFUL } from '../constants/authCacheConstants';
import {
    AUTH_UNAUTHORIZED,
    AuthorityRoleEnum,
    LOAD_AUTH_DATA,
    LOAD_AUTH_DATA_SUCCESSFUL,
    LOGIN_PROCESS,
    LOGIN_PROCESS_SUCCESSFUL,
    LOGOUT_START,
    REDIRECT_TO_SOHO,
} from '../constants/authConstants';
import {
    CHANGE_USER_SERVICE_ACCESS,
    LOGIN_PROCESS_SERVICE_ACCESS,
    LOGIN_USER_SERVICE_ACCESS,
} from '../constants/authServiceAccessConstants';
import { GET_TEMPORARY_PASSWORD, SUBMIT_CHANGE_PASSWORD_FORM_START } from '../constants/changePasswordConstants';
import { INITIAL_RECOVERY, LOGIN_RECOVERY, LOGIN_RECOVERY_ANSWER } from '../constants/recoveryConstants';
import {
    makeSelectAuthFeatures,
    makeSelectNewsServiceAccessLoggedIn,
    makeSelectServiceAccessLoggedIn,
} from '../selectors/authSelectors';
import { IAuthData, IUserFeature } from '../types/authTypes';
import { cacheUserDataStartSaga, cacheUserDataStartSuccessfulSaga } from './authCacheSagas';
import { changeUserServiceAccessSaga, loginUserServiceAccessSaga } from './authServiceAccessSaga';
import { changePasswordSaga, getTemporaryPasswordSaga } from './changePasswordSagas';
import { loginProcessSaga, loginProcessServiceAccessSaga } from './loginProcessSaga';
import { loginRecovery, recoveryAnswer, recoveryReset } from './recoverySaga';

export function* loadAuthDataSuccessfulSaga(): SagaIterator {
    // Check IE11 browser
    const features = yield select(makeSelectAuthFeatures());
    const browseWarning = features.find((feature: IUserFeature) => feature.name === FeatureNameEnum.BROWSER_WARNING);
    if (browseWarning?.enabled && isIE) {
        yield put(
            modalOpen({
                render: () => createElement(BrowserWarningModal),
            }),
        );
    }
}

export function* loadAuthDataSaga(): SagaIterator {
    try {
        const response: IAuthData = yield call(requestSaga, API_HOSTNAME + USER_AUTH, 'GET', null, {
            skipUnauthorizedError: true, // TODO обработать редирект для mode=soho
            throwIfHasError: true,
        });

        if (
            response.authorities &&
            find(response.authorities, { authority: AuthorityRoleEnum.ROLE_PASSWORD_CHANGE_REQUIRED })
        ) {
            yield call(appHistoryPush, RoutePath.changePassword);
            yield put(getTemporaryPassword());
            return;
        }

        if (response.authorities && find(response.authorities, { authority: AuthorityRoleEnum.ROLE_SERVICE_USER })) {
            yield put(loginProcessServiceAccessSuccessfulAction());
        }

        if (
            response.authorities &&
            find(response.authorities, { authority: AuthorityRoleEnum.ROLE_NEWS_SERVICE_USER })
        ) {
            yield put(loginProcessNewsServiceAccessSuccessfulAction());
        }

        const currentUserId = yield select(makeSelectLocalStorageCurrentUserId());

        if (currentUserId !== response.user.susrId) {
            yield put(
                setLocalStorageDataAction({
                    key: ELocalStorageKeys.DASHBOARD_SELECTED_ACCOUNT,
                    value: {
                        id: response.user.accountId.toString(),
                    },
                }),
            );
        }

        yield put(
            setLocalStorageDataAction({
                key: ELocalStorageKeys.CURRENT_USER_ID,
                value: response.user.susrId,
            }),
        );

        const cookies: ICookies = yield select(makeSelectCookies());
        if (cookies[ECookieKey.CACHE_IN_PROGRESS]) {
            if (!response.cached && appHistory.location.pathname !== RoutePath.logout) {
                const translation = yield select(makeSelectTranslation());
                yield put(setTextLoader(translation.common.dataSync));
                yield put(cacheUserDataStart());
                yield take(CACHE_USER_DATA_START_SUCCESSFUL);
            } else {
                yield put(cacheUserDataStartSuccessful());
            }
        }

        yield put(loadAuthDataSuccessful(response));
        yield put(getNotificationsStart());

        if (response.notifications) {
            yield put(
                setNotificationsSettings({
                    showNotifications: response.notifications.showNotifications,
                    showNews: response.notifications.showNews,
                }),
            );
        }
    } catch (error: any) {
        yield put(loadAuthDataFailure(error));
        if (error.code === HttpStatus.FORBIDDEN) {
            yield call(authHandleForbiddenErrorSaga);
            return;
        }

        // Если запрос возвращает 418 код, то значит пользователь СП и необходимо перекинуть на форму выбора абонента
        if (error.code === HttpStatus.I_AM_A_TEAPOT) {
            yield put(modalCloseAll());
            // код 418 означает, что пользователь успешно авторизовался как СП, но не как абонент портала
            yield put(loginProcessServiceAccessSuccessfulAction());
            yield call(appHistoryPush, RoutePath.serviceAccessLogin);
            return;
        }

        let loginUrl = RoutePath.login;
        const query = new URLSearchParams(document.location.search);
        const redirectUrl = query.get('r') || document.location.pathname;

        // service access user code
        if (document.location.pathname === RoutePath.serviceAccessLogin) {
            loginUrl = RoutePath.serviceAccessLogin;
        }

        const isRedirectPath = redirectUrl.length > 1 && redirectUrl !== loginUrl;
        const authSSO: IAuthSSO = yield select(makeSelectAuthSSO());

        if (authSSO?.mode === EModeSSO.soho && error.code === HttpStatus.UNAUTHORIZED) {
            yield call(appExternalRedirect, authSSO.login);
        } else {
            yield call(appHistoryPush, `${loginUrl}${isRedirectPath ? `?r=${redirectUrl}` : ''}`);
        }
    }
}

export function* logoutSaga(): SagaIterator {
    const isServiceAccessLoggedIn = yield select(makeSelectServiceAccessLoggedIn());
    const isNewsServiceAccessLoggedIn = yield select(makeSelectNewsServiceAccessLoggedIn());
    const isRedirectToServiceLogin = isServiceAccessLoggedIn || isNewsServiceAccessLoggedIn;
    const authSSO: IAuthSSO = yield select(makeSelectAuthSSO());
    try {
        yield put(removeStorageDataStart());
        yield call(requestSaga, API_LOGOUT, 'GET');
        yield put(logoutStartSuccessful());
        yield put(closeNotificationAction());
        if (authSSO.mode === EModeSSO.sso) {
            window.location.href = authSSO.logout; // ie11 target _self
        }
    } catch (e: any) {
        yield put(logoutStartFailure(e));
    } finally {
        if (authSSO?.mode === EModeSSO.soho) {
            yield call(appExternalRedirect, authSSO.login);
        } else {
            yield call(appHistoryPush, isRedirectToServiceLogin ? RoutePath.serviceAccessLogin : RoutePath.login);
        }
    }
}

export function* redirectToSohoSaga(): SagaIterator {
    const authSSO: IAuthSSO = yield select(makeSelectAuthSSO());
    if (authSSO.mode !== EModeSSO.soho) return;
    try {
        yield call(requestSaga, `${API_LOGOUT}?redirect=true`, 'GET');
        yield put(closeNotificationAction());
        yield call(appExternalRedirect, authSSO.redirect);
    } catch (e: any) {
        yield put(redirectToSohoFailure(e));
        const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
        yield put(
            modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement<IFooterErrorModalProps>(FooterErrorModal, {
                        message: translation.error.codes['redirectToSohoError'],
                        onCloseModal: props.onCloseModal,
                    }),
            }),
        );
    }
}

export function* authHandleForbiddenErrorSaga() {
    yield put(authSetIsForbidden({ isForbidden: true }));
    yield call(appHistoryPush, RoutePath.forbidden);
}

export function* unauthorizedSaga() {
    yield put(modalCloseAll());
    yield put(logoutStartFailure('Unauthorized'));

    yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);

    const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
    yield put(
        openNotification({
            message: translation.error.codes['unauthorized'],
            level: ENotificationLevelTypes.ERROR,
        }),
    );
}

export function* rootAuthSaga(): SagaIterator {
    yield all([
        takeEvery(AUTH_UNAUTHORIZED, unauthorizedSaga),
        takeEvery(LOGIN_PROCESS, loginProcessSaga),
        takeEvery(LOGIN_PROCESS_SERVICE_ACCESS, loginProcessServiceAccessSaga),
        takeEvery(LOGIN_USER_SERVICE_ACCESS, loginUserServiceAccessSaga),
        takeEvery(LOGIN_RECOVERY, loginRecovery),
        takeEvery(LOAD_AUTH_DATA, loadAuthDataSaga),
        takeEvery([LOAD_AUTH_DATA_SUCCESSFUL, LOGIN_PROCESS_SUCCESSFUL], loadAuthDataSuccessfulSaga),
        takeEvery(LOGOUT_START, logoutSaga),
        takeEvery(REDIRECT_TO_SOHO, redirectToSohoSaga),
        takeEvery(CHANGE_USER_SERVICE_ACCESS, changeUserServiceAccessSaga),
        takeEvery(LOGIN_RECOVERY_ANSWER, recoveryAnswer),
        takeEvery(INITIAL_RECOVERY, recoveryReset),
        takeEvery(CACHE_USER_DATA_START, cacheUserDataStartSaga),
        takeEvery(CACHE_USER_DATA_START_SUCCESSFUL, cacheUserDataStartSuccessfulSaga),
        takeEvery(SUBMIT_CHANGE_PASSWORD_FORM_START, changePasswordSaga),
        takeEvery(GET_TEMPORARY_PASSWORD, getTemporaryPasswordSaga),
    ]);
}
